/**
 * Klapp-Bewegungen fuer Browser ohne interpolate-size - also fuer iOS.
 *
 * Die Aufklappfelder (Beschreibung und Personalisierung im Kauffenster,
 * Rabattcode und Notiz im Warenkorb, die Personalisierungsliste an einem
 * Posten) bewegen ihre Hoehe im Stilblatt gegen auto. Das kann der Browser
 * nur mit interpolate-size ausrechnen - Chrome kann es, Safari nicht, dort
 * sprangen die Felder hart auf und zu.
 *
 * Fuer diese Browser haelt das Stilblatt einen zweiten Weg bereit (siehe
 * Abschnitt "Klappen ohne interpolate-size" in divineprint.css): die Hoehe
 * laeuft gegen eine Zahl. Die Zahl liefert dieses Skript - es misst den
 * Inhalt und schreibt das Mass als --dp-klapp an das Feld.
 *
 * Gemessen wird zweimal. Einmal beim Druecken, noch vor dem Aufgehen: das
 * Feld ist da noch zu und traegt kein Polster, deshalb kommt ein kleiner
 * Zuschlag dazu, damit die Bewegung nicht kurz vor dem Ende haengen bleibt.
 * Und einmal nach dem Aufgehen, wenn das Polster anliegt: dieser Wert ist
 * exakt und gilt dann auch fuer das Zugehen.
 *
 * In Browsern mit interpolate-size tut das Skript nichts.
 */

if (!CSS.supports('interpolate-size: allow-keywords')) {
  /** Die Felder, deren Bewegung hier ersetzt wird. */
  const ZIELE =
    '.cart-discount details, .cart-note details, .dp-kauffenster details, details.dp-korb-perso-feld';

  /**
   * @param {HTMLDetailsElement} feld
   * @returns {HTMLElement | null}
   */
  function inhaltVon(feld) {
    if (feld.classList.contains('dp-korb-perso-feld')) {
      return feld.querySelector('.dp-korb-perso');
    }
    return feld.querySelector('.details-content');
  }

  /**
   * Schreibt das gemessene Mass an das Feld.
   *
   * scrollHeight zaehlt das Polster des Elements mit - im zugeklappten
   * Zustand steht es auf null, deshalb der Zuschlag. Ein zu grosses Ziel ist
   * unkritisch: die Hoehe erreicht ihren echten Wert frueher und steht dann.
   *
   * @param {HTMLDetailsElement} feld
   * @param {number} zuschlag
   */
  function miss(feld, zuschlag) {
    const inhalt = inhaltVon(feld);
    if (!inhalt) return;

    /*
     * Mit gehobener Grenze messen: der Inhalt steht unter einer Hoechsthoehe
     * mit beschnittenem Ueberlauf, und ein beschnittener Kasten nennt bei
     * scrollHeight nur seine sichtbare Hoehe - das alte Mass schrieb sich so
     * selbst fest, und ein einzelnes Feld blieb rollbar, weil sein Kasten
     * ein paar Pixel zu knapp war. Fuer die Messung gilt kurz kein Mass.
     */
    const vorher = feld.style.getPropertyValue('--dp-klapp');
    feld.style.setProperty('--dp-klapp', '9999px');
    const mass = inhalt.scrollHeight;
    if (vorher) feld.style.setProperty('--dp-klapp', vorher);
    else feld.style.removeProperty('--dp-klapp');
    if (!mass) return;

    feld.style.setProperty('--dp-klapp', mass + zuschlag + 'px');
  }

  /*
   * Vor dem Aufgehen: der Klick auf den Knopf oder die Zeile kommt hier in
   * der Einfangphase an, bevor irgendetwas umschaltet. Die Reiterknoepfe des
   * Kauffensters schalten ihre Felder selbst um - bei ihnen werden alle
   * Felder des Fensters gemessen.
   */
  document.addEventListener(
    'click',
    (ereignis) => {
      if (!(ereignis.target instanceof Element)) return;

      const knopf = ereignis.target.closest('.dp-reiter__knopf');
      if (knopf) {
        const fenster = knopf.closest('.dp-kauffenster');
        if (fenster) {
          for (const feld of fenster.querySelectorAll('details')) miss(feld, 48);
        }
        return;
      }

      const zeile = ereignis.target.closest('summary');
      const feld = zeile?.closest(ZIELE);
      if (feld instanceof HTMLDetailsElement) miss(feld, feld.classList.contains('dp-korb-perso-feld') ? 16 : 48);
    },
    true
  );

  /*
   * Nach dem Aufgehen: jetzt liegt das Polster an, und scrollHeight nennt das
   * exakte Mass. Es ersetzt den Schaetzwert mitten in der Bewegung - die
   * laeuft dadurch nur auf ihr neues Ziel weiter - und stimmt dann auch fuer
   * das Zugehen. toggle steigt nicht auf, deshalb die Einfangphase.
   */
  /*
   * Der Inhalt eines offenen Feldes aendert sich auch spaeter noch: die
   * Felder von IMPRINT ziehen erst nach dem Laden in den Reiter ein, ein
   * Regler kommt dazu, eine Zeile bricht um. Das alte Mass war dann zu knapp,
   * und ein einzelnes Feld liess sich rollen. Nach jeder Aenderung im
   * Kauffenster wird deshalb jedes offene Feld neu gemessen - gesammelt, ein
   * Bild spaeter.
   */
  let nachmessen = 0;
  const messeOffene = () => {
    nachmessen = 0;
    for (const feld of document.querySelectorAll('.dp-kauffenster details[open]')) miss(feld, 0);
    /*
     * Das Messen schreibt selbst ans style-Attribut der Felder (miss: kurz
     * 9999px, dann zurueck). Diese eigenen Schreibzuege duerfen den Beobachter
     * nicht wieder wecken - sonst messen sich die offenen Felder Bild fuer Bild
     * in Endlosschleife (jeweils mit erzwungener Layout-Berechnung), und auf
     * dem iPad kippt die Seite nach Minuten. Darum werden sie hier verworfen.
     */
    if (beobachter) beobachter.takeRecords();
  };
  const kauffenster = document.querySelector('.dp-kauffenster');
  let beobachter = null;
  if (kauffenster) {
    beobachter = new MutationObserver(() => {
      if (nachmessen) return;
      nachmessen = window.requestAnimationFrame(messeOffene);
    });
    beobachter.observe(kauffenster, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  }

  document.addEventListener(
    'toggle',
    (ereignis) => {
      const feld = ereignis.target;
      if (!(feld instanceof HTMLDetailsElement) || !feld.open) return;
      if (!feld.matches(ZIELE)) return;

      miss(feld, 0);
    },
    true
  );

  /*
   * Ein Feld kann sich auch fuellen, waehrend es schon offen steht.
   *
   * Der Fall aus dem Warenkorb: das Fach "Rabattcode" ist aufgeklappt, der
   * Kunde gibt einen Versandcode ein, und die Zeile dazu trifft erst mit der
   * Antwort des Warenkorbs ein (dp-rabatt.js). Gemessen wurde aber beim
   * Aufklappen - die Hoechsthoehe kannte die neue Zeile nicht, und weil der
   * Textkasten seinen Ueberlauf beschneidet, lag sie unsichtbar darunter.
   * Der eingegebene Code sah dadurch aus, als sei er verschwunden.
   *
   * In Chrome fiel das nie auf: dort laeuft der andere Zweig, die Hoehe
   * faehrt gegen auto und waechst von selbst mit.
   *
   * Wer also etwas in ein offenes Feld haengt, meldet es mit diesem
   * Ereignis - gemessen wird weiterhin nur an einer Stelle.
   */
  document.addEventListener('dp:klapp-neu', (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;
    const feld = ziel.closest(ZIELE);
    if (feld instanceof HTMLDetailsElement && feld.open) miss(feld, 0);
  });
}
